import React, { useCallback, useMemo } from "react";

import { Stack } from "@mui/system";
import { format, milliseconds, parseISO } from "date-fns";
import { orderBy } from "lodash";

import useDeleteAllStagedOrders from "@fartherfinance/frontend/api/Investment/hooks/useDeleteAllStagedOrders";
import useStagedOrders from "@fartherfinance/frontend/api/Investment/hooks/useStagedOrders";

import { toSortable } from "@src/multiCustodian/components/Client/Accounts/Holdings/Components/BreakdownTable/HoldingsTable";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import FullDataTable from "@src/sharedComponents/Table/FullDataTable";
import { Cell, Row } from "@src/sharedComponents/Table/Types";

const columns = [
  "Order Id",
  "Ticker",
  "Cusip",
  "Quantity",
  "Side",
  "Custodian",
  "Custodian Account",
  "Trade Date",
] as const;
type Keys = typeof columns[number];

const StagedOrders: React.FC = () => {
  const [updatedAt, setUpdatedAt] = React.useState<Date | null>(null);

  const notification = useStatusNotification();

  const auth = useAdvisorRequestAuth();

  const deleteAllOrders = useDeleteAllStagedOrders(auth);

  const doPrefetch = false;
  const orders = useStagedOrders(auth, doPrefetch, {
    refetchInterval: milliseconds({ seconds: 5 }),
  });

  const orderRows = useMemo((): null | Row<Keys, Cell>[] => {
    if (orders.hasError || orders.isLoading) {
      return null;
    }

    setUpdatedAt(new Date());

    // We don't know if the backend will return the orders in order, so we sort them here to get a consistent memo-ing
    const ordered = orderBy(
      orders.data.orders,
      [(o) => o.clientOrderId],
      ["asc"]
    );

    return ordered.map(
      (order): Row<Keys, Cell> => ({
        key: order.clientOrderId,
        "Order Id": { value: order.clientOrderId },
        Ticker: { value: order.ticker },
        Cusip: { value: order.cusip },
        Quantity: {
          value: order.quantity.toNumber().toLocaleString("en-US"),
          fullValue: toSortable(order.quantity.toNumber()),
        },
        Side: { value: order.side },
        Custodian: { value: order.custodian },
        "Custodian Account": { value: order.custodianAccountId },
        "Trade Date": {
          value: format(parseISO(order.tradeDate), "MM/dd/yyyy"),
          fullValue: parseISO(order.tradeDate).toISOString(),
        },
      })
    );
  }, [orders]);

  const deleteAllStagedOrders = useCallback(async () => {
    // put real code here

    await deleteAllOrders();

    notification("All staged orders have been deleted", "Success");
  }, [notification, deleteAllOrders]);

  return (
    <Stack direction="column" gap={2}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        gap={5}
      >
        <div>
          Updated at {updatedAt === null ? "not yet" : format(updatedAt, "pp")}
        </div>
        <Button
          variant="contained"
          text="Delete all staged orders"
          buttonType="error"
          onClick={deleteAllStagedOrders}
        />
      </Stack>

      {orderRows === null ? (
        <FullDataTable
          key="StagedOrdersLoading"
          isLoading={true}
          columns={columns}
          defaultSortColumn={["Trade Date", "asc"]}
          secondaryDefaultSortColumn={["Ticker", "asc"]}
          rows={Array.from({ length: 10 }).map(
            (_, i): Row<Keys, Cell> => ({
              key: i.toString(),
              "Order Id": { value: <Skeleton /> },
              Ticker: { value: <Skeleton /> },
              Cusip: { value: <Skeleton /> },
              Quantity: { value: <Skeleton /> },
              Side: { value: <Skeleton /> },
              Custodian: { value: <Skeleton /> },
              "Custodian Account": { value: <Skeleton /> },
              "Trade Date": { value: <Skeleton /> },
            })
          )}
          emptyCell={<Skeleton />}
        />
      ) : (
        <FullDataTable
          key={`StagedOrders${orderRows.length}`}
          isLoading={false}
          columns={columns}
          defaultSortColumn={["Trade Date", "asc"]}
          rows={orderRows}
          emptyCell={<Skeleton />}
          hidePagination
          defaultRowsPerPage={orderRows.length}
        />
      )}
    </Stack>
  );
};

export default StagedOrders;
